import React, { useState, useCallback } from "react";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Divider,
  CircularProgress,
  Container,
  Fade,
  IconButton,
  Dialog,
  DialogContent,
} from "@mui/material";
import {
  AdminPanelSettings,
  ErrorOutline,
  Download as DownloadIcon,
  Close,
  PhoneAndroid,
} from "@mui/icons-material";
import {
  signInWithPopup,
  GoogleAuthProvider,
  setPersistence,
  browserLocalPersistence,
  signOut,
} from "firebase/auth";
import { auth } from "../firebase";
import colors from "../constants/colors";
import { URL } from "../constants/url";
import googleLogo from "../assets/google.png";

/* ✅ Moved outside & memoized to prevent re-renders */
const LoginContent = React.memo(
  ({
    isModal,
    loading,
    email,
    error,
    notAdmin,
    colors,
    handleClose,
    setEmail,
    handleEmailLogin,
    handleGoogleLogin,
    handleBackToLogin,
    // OTP props
    phone,
    setPhone,
    otp,
    setOtp,
    otpFlow,
    otpSent,
    otpLoading,
    handleStartOtp,
    handleSendOtp,
    handleVerifyOtp,
    handleResendOtp,
    handleCancelOtp,
  }) => (
    <Paper
      elevation={8}
      sx={{
        p: 4,
        borderRadius: 4,
        backgroundColor: colors.white,
        border: `2px solid ${colors.green}25`,
        boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
        transition: "0.3s ease",
        backdropFilter: "blur(6px)",
        "&:hover": isModal
          ? {}
          : {
              boxShadow: "0 14px 35px rgba(0,0,0,0.12)",
              borderColor: `${colors.green}70`,
              transform: "translateY(-2px)",
            },
        position: "relative",
      }}
    >
      {/* Close button for modal */}
      {isModal && (
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            color: colors.darkGray,
            "&:hover": {
              backgroundColor: colors.lightGray,
            },
          }}
        >
          <Close />
        </IconButton>
      )}

      {!notAdmin ? (
        <>
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 70,
                height: 70,
                borderRadius: "50%",
                background: `linear-gradient(145deg, ${colors.lightGreen}, ${colors.green}55)`,
                mb: 2,
                boxShadow: "0 4px 10px rgba(0,0,0,0.08)",
              }}
            >
              <AdminPanelSettings
                sx={{ fontSize: 36, color: colors.darkGreen }}
              />
            </Box>

            <Typography variant="h5" fontWeight={700} color={colors.darkBlack}>
              OK Market Admin
            </Typography>
            <Typography variant="body2" color={colors.darkGray}>
              Access your admin dashboard securely
            </Typography>
          </Box>

          {error && (
            <Typography
              variant="body2"
              color="error"
              sx={{ mb: 2, textAlign: "center", fontWeight: 500 }}
            >
              {error}
            </Typography>
          )}

          {/* <form onSubmit={handleEmailLogin}>
            <TextField
              fullWidth
              label="Admin Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
              disabled={loading}
              placeholder="admin@gmail.com"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2.5,
                  backgroundColor: `${colors.lightGray}`,
                  "&:hover fieldset": { borderColor: colors.green },
                  "&.Mui-focused fieldset": {
                    borderColor: colors.green,
                    borderWidth: 2,
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: colors.green,
                },
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading || !email}
              sx={{
                mt: 2,
                py: 1.2,
                borderRadius: 2.5,
                backgroundColor: colors.green,
                fontWeight: 600,
                textTransform: "none",
                letterSpacing: 0.3,
                "&:hover": { backgroundColor: colors.darkGreen },
              }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: colors.white }} />
              ) : (
                "Login"
              )}
            </Button>
          </form>

          <Box sx={{ my: 3, position: "relative" }}>
            <Divider sx={{ borderColor: colors.lightGray }} />
            <Typography
              variant="body2"
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                backgroundColor: colors.white,
                px: 1.4,
                color: colors.midGray,
                fontWeight: 500,
              }}
            >
              or
            </Typography>
          </Box> */}

          <Button
            fullWidth
            variant="outlined"
            onClick={handleGoogleLogin}
            disabled={loading}
            sx={{
              py: 1.2,
              borderRadius: 2.5,
              borderColor: colors.midGray,
              color: colors.darkGray,
              fontWeight: 600,
              textTransform: "none",
              display: "flex",
              alignItems: "center",
              gap: 1.2,
              justifyContent: "center",
              "&:hover": {
                borderColor: colors.green,
                backgroundColor: `${colors.lightGreen}30`,
              },
            }}
          >
            <img src={googleLogo} alt="Google" width="22" />
            Sign in with Google
          </Button>

          {/* OTP flow */}
          {!otpFlow ? (
            <Button
              fullWidth
              variant="outlined"
              startIcon={<PhoneAndroid />}
              onClick={handleStartOtp}
              disabled={loading}
              sx={{
                mt: 2,
                py: 1.2,
                borderRadius: 2.5,
                borderColor: colors.midGray,
                color: colors.darkGray,
                fontWeight: 600,
                textTransform: "none",
              }}
            >
              Sign in with OTP
            </Button>
          ) : (
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Mobile Number"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/[^0-9]/g, ""))}
                margin="normal"
                disabled={otpLoading}
                placeholder="Enter 10 digit mobile number"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2.5,
                    backgroundColor: `${colors.lightGray}`,
                    "&:hover fieldset": { borderColor: colors.green },
                    "&.Mui-focused fieldset": {
                      borderColor: colors.green,
                      borderWidth: 2,
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: colors.green,
                  },
                }}
              />

              {!otpSent ? (
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={otpLoading || phone.length < 10}
                  onClick={handleSendOtp}
                  sx={{
                    mt: 1,
                    py: 1.2,
                    borderRadius: 2.5,
                    backgroundColor: colors.green,
                    fontWeight: 600,
                    textTransform: "none",
                    letterSpacing: 0.3,
                    "&:hover": { backgroundColor: colors.darkGreen },
                  }}
                >
                  {otpLoading ? (
                    <CircularProgress size={20} sx={{ color: colors.white }} />
                  ) : (
                    "Send OTP"
                  )}
                </Button>
              ) : (
                <>
                  <TextField
                    fullWidth
                    label="Enter OTP"
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ""))}
                    margin="normal"
                    disabled={otpLoading}
                    placeholder="6 digit OTP"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2.5,
                        backgroundColor: `${colors.lightGray}`,
                        "&:hover fieldset": { borderColor: colors.green },
                        "&.Mui-focused fieldset": {
                          borderColor: colors.green,
                          borderWidth: 2,
                        },
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: colors.green,
                      },
                    }}
                  />

                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={otpLoading || otp.length < 4}
                    onClick={handleVerifyOtp}
                    sx={{
                      mt: 1,
                      py: 1.2,
                      borderRadius: 2.5,
                      backgroundColor: colors.green,
                      fontWeight: 600,
                      textTransform: "none",
                      letterSpacing: 0.3,
                      "&:hover": { backgroundColor: colors.darkGreen },
                    }}
                  >
                    {otpLoading ? (
                      <CircularProgress size={20} sx={{ color: colors.white }} />
                    ) : (
                      "Verify OTP"
                    )}
                  </Button>

                  <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                    <Button variant="text" onClick={handleResendOtp} disabled={otpLoading} sx={{ textTransform: 'none' }}>
                      Resend OTP
                    </Button>

                    <Button variant="text" onClick={handleCancelOtp} disabled={otpLoading} sx={{ textTransform: 'none' }}>
                      Cancel
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          )}

          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Typography variant="caption" sx={{ color: colors.darkGray }}>
              This portal is for authorized OK Market admins only.
            </Typography>
          </Box>
        </>
      ) : (
        <Fade in={notAdmin} timeout={400}>
          <Box
            sx={{
              textAlign: "center",
              p: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <ErrorOutline sx={{ fontSize: 58, color: colors.orange, mb: 2 }} />

            <Typography variant="h6" fontWeight={700} color={colors.darkBlack}>
              Access Restricted
            </Typography>

            <Typography
              variant="body1"
              sx={{
                maxWidth: 310,
                mb: 3,
                lineHeight: 1.6,
                color: colors.darkGray,
                fontWeight: 500,
              }}
            >
              This portal is only for{" "}
              <strong style={{ color: colors.green }}>OK Market Admins</strong>.
              Please download the{" "}
              <strong style={{ color: colors.green }}>OK Market App</strong>{" "}
              from Play Store.
            </Typography>

            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              href="https://play.google.com/store/apps/details?id=com.ChaitanyaAgro.Okmarket"
              target="_blank"
              rel="noopener"
              sx={{
                backgroundColor: colors.green,
                textTransform: "none",
                borderRadius: 2.5,
                px: 3,
                py: 1.2,
                fontWeight: 600,
                letterSpacing: 0.3,
                "&:hover": { backgroundColor: colors.darkGreen },
              }}
            >
              Download App
            </Button>

            <Button
              variant="text"
              sx={{
                mt: 2,
                color: colors.darkGray,
                textTransform: "none",
                "&:hover": { textDecoration: "underline" },
              }}
              onClick={handleBackToLogin}
            >
              Back to Login
            </Button>
          </Box>
        </Fade>
      )}
    </Paper>
  )
);

const Login = ({ onLogin, onClose, isModal = false }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [notAdmin, setNotAdmin] = useState(false);
  const [error, setError] = useState("");

  // OTP states
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpFlow, setOtpFlow] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);


  const checkUserRoleByEmail = async (email) => {
    try {
      const response = await fetch(`${URL}/api/users/role/${email}`);
      const data = await response.json();
      if (!response.ok)
        throw new Error(data.message || "Failed to check user role");
      return data;
    } catch (error) {
      throw new Error(error.message || "Network error occurred");
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setNotAdmin(false);

    try {
      const result = await checkUserRoleByEmail(email);
      if (!email.endsWith("@gmail.com")) {
        setError("Only Gmail accounts are allowed");
        setLoading(false);
        return;
      }

      if (result.success && result.role === "admin") {
        const userData = {
          email,
          role: result.role,
          userData: result.data,
        };
        onLogin(userData);
      } else {
        setNotAdmin(true);
      }
    } catch (err) {
      if (err.message.includes("not found")) {
        setNotAdmin(true);
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError("");
    setNotAdmin(false);

    try {
      await setPersistence(auth, browserLocalPersistence);

      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);
      const user = userCredential.user;

      if (!user.email.endsWith("@gmail.com")) {
        await signOut(auth);
        setError("Only Gmail accounts are allowed");
        setLoading(false);
        return;
      }

      const result = await checkUserRoleByEmail(user.email);

      if (result.success && result.role === "admin") {
        onLogin({
          email: user.email,
          role: result.role,
          userData: result.data,
          firebaseUser: user,
        });
      } else {
        await signOut(auth);
        setNotAdmin(true);
      }
    } catch (err) {
      if (err.code !== "auth/popup-closed-by-user") {
        setError(err.message || "Google login failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  const handleBackToLogin = () => {
    setNotAdmin(false);
    setEmail("");
  };

  // OTP handlers
  const handleStartOtp = () => {
    setError("");
    setOtpFlow(true);
    setOtpSent(false);
    setPhone("");
    setOtp("");
  };

  const handleCancelOtp = () => {
    setOtpFlow(false);
    setOtpSent(false);
    setPhone("");
    setOtp("");
    setError("");
  };

  const handleSendOtp = async () => {
    setOtpLoading(true);
    setError("");
    setNotAdmin(false);

    try {
      const res = await fetch(`${URL}/api/admin/otp/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.message && data.message.toLowerCase().includes("not assigned")) {
          setNotAdmin(true);
        } else {
          setError(data.message || "Failed to send OTP");
        }
        setOtpLoading(false);
        return;
      }
      setOtpSent(true);
    } catch (err) {
      setError(err.message || "Network error");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResendOtp = async () => {
    // same as send
    await handleSendOtp();
  };

  const handleVerifyOtp = async () => {
    setOtpLoading(true);
    setError("");
    try {
      const res = await fetch(`${URL}/api/admin/otp/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone, otp }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.message || "OTP verification failed");
        setOtpLoading(false);
        return;
      }

      // success - call onLogin with user data
      if (onLogin) {
        onLogin({ phone, role: "admin", userData: data.data });
      }
    } catch (err) {
      setError(err.message || "Network error");
    } finally {
      setOtpLoading(false);
    }
  };


  // ✅ If it's a modal, use keepMounted to prevent reinitialization
  if (isModal) {
    return (
      <Dialog
        open={true}
        onClose={handleClose}
        keepMounted
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
            overflow: "visible",
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <LoginContent
            isModal={isModal}
            loading={loading}
            email={email}
            error={error}
            notAdmin={notAdmin}
            colors={colors}
            handleClose={handleClose}
            setEmail={setEmail}
            handleEmailLogin={handleEmailLogin}
            handleGoogleLogin={handleGoogleLogin}
            handleBackToLogin={handleBackToLogin}
            // OTP props
            phone={phone}
            setPhone={setPhone}
            otp={otp}
            setOtp={setOtp}
            otpFlow={otpFlow}
            otpSent={otpSent}
            otpLoading={otpLoading}
            handleStartOtp={handleStartOtp}
            handleSendOtp={handleSendOtp}
            handleVerifyOtp={handleVerifyOtp}
            handleResendOtp={handleResendOtp}
            handleCancelOtp={handleCancelOtp}
          />
        </DialogContent>
      </Dialog>
    );
  }

  // Full screen login page
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        background: `linear-gradient(135deg, ${colors.lightMint} 0%, ${colors.white} 100%)`,
      }}
    >
      <Container maxWidth="xs">
        <LoginContent
          isModal={isModal}
          loading={loading}
          email={email}
          error={error}
          notAdmin={notAdmin}
          colors={colors}
          handleClose={handleClose}
          setEmail={setEmail}
          handleEmailLogin={handleEmailLogin}
          handleGoogleLogin={handleGoogleLogin}
          handleBackToLogin={handleBackToLogin}
        />
      </Container>
    </Box>
  );
};

export default Login;
